// Thin wrapper around Amplify Auth for cleaner call sites.
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  fetchAuthSession,
  getCurrentUser,
} from "aws-amplify/auth";

export interface AuthedUser {
  userId: string;
  email: string;
}

export async function currentUser(): Promise<AuthedUser | null> {
  try {
    const u = await getCurrentUser();
    return { userId: u.userId, email: u.signInDetails?.loginId ?? "" };
  } catch {
    return null;
  }
}

export async function idToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

// Force Amplify to swap the refresh token for a fresh access + ID token.
// Returns the new ID token, or null if the refresh token is itself invalid or
// missing (in which case the caller should sign the user out).
export async function forceRefreshTokens(): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export interface SessionInfo {
  email: string;
  userId: string;
  // Access token expiry (seconds until it expires). Present when a valid session
  // is loaded; null when the user is signed out.
  accessExpiresInSec: number | null;
  // Best-effort refresh-token issue time (ms since epoch). Cognito refresh
  // tokens have a fixed lifetime configured on the app client (30 days for
  // this pool). Null when we cannot read it from the current session.
  refreshIssuedAtMs: number | null;
}

export async function sessionInfo(): Promise<SessionInfo | null> {
  try {
    const [u, session] = await Promise.all([getCurrentUser(), fetchAuthSession()]);
    const accessExp = session.tokens?.accessToken?.payload?.exp;
    const authTime = session.tokens?.idToken?.payload?.auth_time;
    const nowSec = Math.floor(Date.now() / 1000);
    return {
      email: u.signInDetails?.loginId ?? "",
      userId: u.userId,
      accessExpiresInSec: typeof accessExp === "number" ? Math.max(0, accessExp - nowSec) : null,
      refreshIssuedAtMs: typeof authTime === "number" ? authTime * 1000 : null,
    };
  } catch {
    return null;
  }
}

// Amplify v6 throws UserAlreadyAuthenticatedException when a stale session is
// still held in browser storage. We handle that by silently signing out and
// retrying once so the user never sees the raw exception.
function isAlreadyAuthenticatedError(err: unknown): boolean {
  if (!err) return false;
  const anyErr = err as { name?: string; message?: string };
  return (
    anyErr.name === "UserAlreadyAuthenticatedException" ||
    /already a signed in user/i.test(anyErr.message ?? "")
  );
}

// Nuke any lingering Cognito state in browser storage. Amplify v6 keeps its
// session in localStorage/sessionStorage under CognitoIdentityServiceProvider.*
// keys. Even after amplifySignOut() throws, wiping those keys manually is
// enough to unblock a fresh signIn().
function purgeCognitoStorage(): void {
  try {
    const wipe = (storage: Storage) => {
      const keys: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i);
        if (k && /CognitoIdentityServiceProvider|amplify-signin-with-hostedUI|amplify-redirected-from-hosted-ui/i.test(k)) {
          keys.push(k);
        }
      }
      keys.forEach(k => storage.removeItem(k));
    };
    if (typeof window !== "undefined") {
      wipe(window.localStorage);
      wipe(window.sessionStorage);
    }
  } catch { /* ignore storage access errors */ }
}

// Fully reset Amplify's session state — both in-memory and on-disk. Amplify
// v6 caches the auth session in memory after configure(), so wiping storage
// alone is not enough. amplifySignOut() flushes both. If it throws (some
// v6 versions do when the session is already corrupt), fall back to storage
// wipe alone — next getCurrentUser() call will fail cleanly and Amplify
// re-hydrates from empty storage.
async function fullReset(): Promise<void> {
  try {
    await amplifySignOut({ global: false });
  } catch {
    // Amplify v6 may throw when session is already corrupt. Storage wipe
    // below is enough — next getCurrentUser() will fail cleanly and Amplify
    // re-hydrates from empty storage.
  }
  purgeCognitoStorage();
}

export async function signIn(email: string, password: string) {
  const attempt = () => amplifySignIn({ username: email, password });
  try {
    const r = await attempt();
    if (r.isSignedIn) return { ok: true as const };
    return { ok: false as const, nextStep: r.nextStep };
  } catch (err) {
    if (isAlreadyAuthenticatedError(err)) {
      await fullReset();
      try {
        const r = await attempt();
        if (r.isSignedIn) return { ok: true as const };
        return { ok: false as const, nextStep: r.nextStep };
      } catch (retryErr) {
        if (isAlreadyAuthenticatedError(retryErr)) {
          const e = new Error("Session state is stuck. Please close this tab, open a fresh window, and try again.");
          (e as any).name = "StaleSessionError";
          throw e;
        }
        throw retryErr;
      }
    }
    throw err;
  }
}

// Called on SignIn page mount to defensively clear any lingering session
// before the user types credentials. Safe no-op if already signed out.
export async function clearAnyExistingSession(): Promise<void> {
  await fullReset();
}

export async function signUp(email: string, password: string) {
  const r = await amplifySignUp({
    username: email,
    password,
    options: { userAttributes: { email } },
  });
  return { userId: r.userId, nextStep: r.nextStep };
}

export async function confirmSignUp(email: string, code: string) {
  const r = await amplifyConfirmSignUp({ username: email, confirmationCode: code });
  return { isComplete: r.isSignUpComplete };
}

export async function resendCode(email: string) {
  await resendSignUpCode({ username: email });
}

export async function signOut() {
  await amplifySignOut();
}

export async function requestPasswordReset(email: string) {
  const r = await amplifyResetPassword({ username: email });
  return { nextStep: r.nextStep };
}

export async function confirmPasswordReset(email: string, code: string, newPassword: string) {
  await amplifyConfirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  });
}
